import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useShop, useI18n } from "@/context/Shop";
import { useDebounced } from "@/hooks/useFetch";
import { api, media } from "@/lib/api";
import { brandPath, catPath, deptPath, productPath } from "@/lib/nav";
import { PriceInline } from "@/components/store/Price";

const RS_KEY = "lc_recent_searches";

export function SearchOverlay() {
  const { searchOpen, setSearchOpen, nav } = useShop();
  const { t, tr, tp } = useI18n();
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 220);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const recent = (() => { try { return JSON.parse(localStorage.getItem(RS_KEY) || "[]"); } catch { return []; } })();

  useEffect(() => {
    if (dq.trim().length < 2) { setRes(null); return; }
    setLoading(true);
    api.get("/store/search/suggest", { params: { q: dq } }).then((r) => setRes(r.data)).catch(() => setRes({ products: [], brands: [], categories: [] })).finally(() => setLoading(false));
  }, [dq]);
  useEffect(() => {
    if (!searchOpen) { setQ(""); setRes(null); }
  }, [searchOpen]);

  const go = (term) => {
    const tm = (term || q).trim();
    if (!tm) return;
    try { localStorage.setItem(RS_KEY, JSON.stringify([tm, ...recent.filter((x) => x !== tm)].slice(0, 6))); } catch { /* ignore */ }
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(tm)}`);
  };
  const close = () => setSearchOpen(false);
  const empty = res && !res.products.length && !res.brands.length && !res.categories.length;
  const popularCats = (nav?.departments || []).flatMap((d) => (d.children || []).filter((c) => c.count > 0).slice(0, 2).map((c) => ({ ...c, dept: d.slug }))).slice(0, 6);

  return (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent className="max-w-3xl p-0 gap-0 bg-background rounded-none top-0 sm:top-[10%] translate-y-0 h-full sm:h-auto max-h-full sm:max-h-[80vh] flex flex-col [&>button]:hidden" data-testid="search-overlay">
        <DialogTitle className="sr-only">{t("search")}</DialogTitle>
        <form onSubmit={(e) => { e.preventDefault(); go(); }} className="flex items-center gap-3 px-5 sm:px-6 border-b h-16 shrink-0">
          <Search className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} className="flex-1 bg-transparent outline-none text-base sm:text-lg placeholder:text-muted-foreground" data-testid="search-input" aria-label={t("search")} />
          <button type="button" aria-label={t("close")} onClick={close} className="p-2 hover:bg-secondary" data-testid="search-close-button"><X className="h-4 w-4" /></button>
        </form>
        <div className="p-5 sm:p-6 overflow-y-auto">
          {q.trim().length < 2 ? (
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <p className="eyebrow mb-3">{t("recent_searches")}</p>
                {recent.length ? recent.map((r) => <button key={r} onClick={() => go(r)} className="block text-sm py-1 hover:underline underline-offset-4" data-testid="search-recent-item">{r}</button>) : <p className="text-sm text-muted-foreground">{t("no_recent")}</p>}
                <p className="eyebrow mb-3 mt-8">{t("popular_categories")}</p>
                {popularCats.map((c) => <Link key={c.dept + c.slug} to={catPath(c.dept, c.slug)} onClick={close} className="block text-sm py-1 hover:underline underline-offset-4">{tr(c)}</Link>)}
              </div>
              <div>
                <p className="eyebrow mb-3">{t("popular_brands")}</p>
                {(nav?.brands || []).filter((b) => b.has_products).slice(0, 8).map((b) => <Link key={b.slug} to={brandPath(b.slug)} onClick={close} className="block text-sm py-1 hover:underline underline-offset-4" data-testid="search-popular-brand">{b.name}</Link>)}
              </div>
            </div>
          ) : loading && !res ? <p className="text-sm text-muted-foreground">{t("searching")}</p> : empty ? (
            <div data-testid="search-no-results">
              <p className="text-sm text-muted-foreground">{t("no_results")} “{q}”.</p>
              <div className="grid sm:grid-cols-2 gap-8 mt-6">
                <div><p className="eyebrow mb-3">{t("popular_categories")}</p>{popularCats.map((c) => <Link key={c.dept + c.slug} to={catPath(c.dept, c.slug)} onClick={close} className="block text-sm py-1 hover:underline underline-offset-4">{tr(c)}</Link>)}</div>
                <div><p className="eyebrow mb-3">{t("popular_brands")}</p>{(nav?.brands || []).filter((b) => b.has_products).slice(0, 6).map((b) => <Link key={b.slug} to={brandPath(b.slug)} onClick={close} className="block text-sm py-1 hover:underline underline-offset-4">{b.name}</Link>)}</div>
              </div>
            </div>
          ) : res && (
            <div className="grid sm:grid-cols-12 gap-8">
              <div className="sm:col-span-4 space-y-6">
                {res.did_you_mean && <p className="text-sm" data-testid="search-did-you-mean">{t("did_you_mean")} <button onClick={() => setQ(res.did_you_mean)} className="font-semibold underline underline-offset-4">{res.did_you_mean}</button>?</p>}
                {res.brands.length > 0 && <div><p className="eyebrow mb-2">{t("brands")}</p>{res.brands.map((b) => <Link key={b.slug} to={brandPath(b.slug)} onClick={close} className="block text-sm py-1 hover:underline underline-offset-4" data-testid="search-suggestion-item">{b.name}</Link>)}</div>}
                {res.categories.length > 0 && <div><p className="eyebrow mb-2">{t("categories")}</p>{res.categories.map((c) => <Link key={c.department + c.slug} to={c.is_department ? deptPath(c.slug) : catPath(c.department, c.slug)} onClick={close} className="block text-sm py-1 hover:underline underline-offset-4" data-testid="search-suggestion-item">{tr(c)}</Link>)}</div>}
              </div>
              <div className="sm:col-span-8">
                <p className="eyebrow mb-3">{t("products")}</p>
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  {res.products.map((p) => (
                    <Link key={p.id} to={productPath(p.slug)} onClick={close} className="group" data-testid="search-result-item">
                      <div className="aspect-[3/4] bg-muted overflow-hidden">{p.primary_image && <img src={media(p.primary_image, 300)} alt="" className="w-full h-full object-cover img-zoom" />}</div>
                      <p className="text-[10px] uppercase tracking-[0.12em] font-semibold mt-2 leading-tight">{p.brand_name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{tp(p, "name")}</p>
                      <PriceInline value={p.min_price} full={p.min_full_price} onSale={p.on_sale} className="text-[11px]" />
                    </Link>
                  ))}
                </div>
                <button onClick={() => go()} className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] font-semibold link-lux" data-testid="search-view-all-button">{t("view_all_results")} <ArrowRight className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
